'use client';

import { Card } from '@repo/ui';
import { ScrollableContainer } from '../common/scrollable-container';
import { DistributionGraph } from '../common/distribution-graph';
import jStat from 'jstat';

export interface ChiSquareResult {
  statistic: number;
  pValue: number;
  degreesOfFreedom: number;
  observed: number[];
  expected: number[];
}

interface ChiSquareCalculatorProps {
  visualizationData: ChiSquareResult | null;
}

export function ChiSquareCalculator({ visualizationData: result }: ChiSquareCalculatorProps) {
  if (!result) {
    return null;
  }

  const { statistic, pValue, degreesOfFreedom, observed, expected } = result;

  // Generate points for chi-square distribution visualization
  const numPoints = 100;
  const maxX = Math.max(statistic * 2, jStat.chisquare.inv(0.99, degreesOfFreedom));
  const points = Array.from({ length: numPoints }, (_, i) => {
    const x = (maxX * i) / (numPoints - 1);
    return jStat.chisquare.pdf(x, degreesOfFreedom);
  });

  // Generate x-axis values
  const xValues = Array.from({ length: numPoints }, (_, i) => (maxX * i) / (numPoints - 1));

  return (
    <ScrollableContainer>
      <div className="space-y-6">
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Chi-square test results</h3>
          <div className="space-y-4">
            <div>
              <p className="font-medium">Test statistic (χ²):</p>
              <p>{statistic.toFixed(4)}</p>
            </div>
            <div>
              <p className="font-medium">P-value:</p>
              <p>{pValue.toFixed(4)}</p>
            </div>
            <div>
              <p className="font-medium">Degrees of freedom:</p>
              <p>{degreesOfFreedom}</p>
            </div>
            <div>
              <p className="font-medium">Interpretation:</p>
              <p>
                {pValue < 0.05
                  ? 'There is significant evidence to reject the null hypothesis (p < 0.05)'
                  : 'There is not enough evidence to reject the null hypothesis (p ≥ 0.05)'}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Chi-square distribution</h3>
          <DistributionGraph
            data={points}
            xAxisValues={xValues}
            title="Chi-square distribution"
            xAxisLabel="χ² value"
            yAxisLabel="Probability density"
            height={300}
            labels={[
              {
                value: statistic,
                text: `χ² = ${statistic.toFixed(4)}`,
                color: '#ff4d4d',
                lineStyle: 'solid'
              },
              {
                value: jStat.chisquare.inv(0.95, degreesOfFreedom),
                text: 'Critical value (α = 0.05)',
                color: '#4d79ff',
                lineStyle: 'dashed'
              }
            ]}
          />
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Frequency comparison</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="text-left p-2">Category</th>
                  <th className="text-left p-2">Observed</th>
                  <th className="text-left p-2">Expected</th>
                  <th className="text-left p-2">Difference</th>
                </tr>
              </thead>
              <tbody>
                {observed.map((obs, index) => {
                  const exp = expected[index] ?? 0;
                  return (
                    <tr key={index}>
                      <td className="p-2">Category {index + 1}</td>
                      <td className="p-2">{obs}</td>
                      <td className="p-2">{exp.toFixed(2)}</td>
                      <td className="p-2">{(obs - exp).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </ScrollableContainer>
  );
} 